import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import useCachedCollection from '../../hooks/useCachedCollection';
import { useAuth } from '../../context/AuthContext';
import { 
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, CircularProgress, Grid, Stack, Dialog, DialogTitle, 
    DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, 
    MenuItem, IconButton 
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import PaymentsIcon from '@mui/icons-material/Payments';
import GlassCard from '../../ui/glass/components/GlassCard';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import { Loading } from '../../components/common/Loading';
import { formatCurrency } from '../../utils/formatters';
import { startOfMonth, endOfMonth, subMonths } from 'date-fns';
import * as XLSX from 'xlsx';

const GIDER_TURLERI = [
    "Sarf Alış",
    "Cihaz Alış",
    "Ofis Gideri",
    "Uyku Testi",
    "GD",
    "Kredi Taksiti",
    "Kargo",
    "Akaryakıt"
];

const StatCard = ({ title, value, color = "inherit" }) => (
    <GlassCard sx={{ p: 2, textAlign: 'center', height: '100%' }}>
      <Typography color="text.secondary">{title}</Typography>
      <Typography variant="h5" fontWeight="bold" sx={{ color }}>{value}</Typography>
    </GlassCard>
);

const GiderTakibi = () => {
    const { currentUser } = useAuth();

    // Filters
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    
    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);

    // Form Stats
    const [giderTuru, setGiderTuru] = useState('');
    const [tutar, setTutar] = useState('');
    const [firmaAdi, setFirmaAdi] = useState('');
    const [tarih, setTarih] = useState(null);
    const [aciklama, setAciklama] = useState('');

    // Giderler paylaşımlı oturum cache'inden gelir; eskiden her açılışta ~520
    // doküman okunup kalıcı realtime listener bırakılıyordu. Ekle/düzenle/sil
    // sonrası refreshExpenses() ile liste anında tazelenir.
    const { data: expensesRaw, loading, refresh: refreshExpenses } = useCachedCollection('expenses');

    const expenses = useMemo(() => {
        const zaman = (d) => (d?.toDate ? d.toDate().getTime() : new Date(d || 0).getTime());
        return [...expensesRaw].sort((a, b) => zaman(b.tarih) - zaman(a.tarih));
    }, [expensesRaw]);

    const filteredExpenses = useMemo(() => {
        return expenses.filter(exp => {
            if (!exp.tarih) return false;
            let d = exp.tarih.toDate ? exp.tarih.toDate() : new Date(exp.tarih);
            
            if (startDate) {
                const start = startDate.toDate ? startDate.toDate() : new Date(startDate);
                start.setHours(0,0,0,0);
                if (d < start) return false;
            }
            if (endDate) {
                const end = endDate.toDate ? endDate.toDate() : new Date(endDate);
                end.setHours(23,59,59,999);
                if (d > end) return false;
            }
            return true;
        });
    }, [expenses, startDate, endDate]);

    const handleDateFilterClick = (start, end) => {
        setStartDate(start);
        setEndDate(end);
    };

    const handleExport = () => {
        const dataToExport = filteredExpenses.map(exp => ({
            'Gider Türü': exp.giderTuru,
            'Tutar': exp.tutar,
            'Firma / İlgili Kişi': exp.firmaAdi,
            'Tarih': exp.tarih?.toDate ? exp.tarih.toDate().toLocaleDateString('tr-TR') : '-',
            'Açıklama': exp.aciklama || '-'
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Gider_Raporu");
        XLSX.writeFile(workbook, "Gider_Raporu.xlsx");
    };

    const summaryData = useMemo(() => {
        let total = 0;
        const categoryTotals = {};

        filteredExpenses.forEach(exp => {
            const amount = Number(exp.tutar) || 0;
            total += amount;

            const category = exp.giderTuru || 'Diğer';
            if (!categoryTotals[category]) categoryTotals[category] = 0;
            categoryTotals[category] += amount;
        });

        const topCategories = Object.entries(categoryTotals)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 4); 

        return { total, topCategories };
    }, [filteredExpenses]);

    const resetForm = () => {
        setGiderTuru('');
        setTutar('');
        setFirmaAdi('');
        setTarih(new Date()); // Defaults to today
        setAciklama('');
        setEditingExpense(null);
        setIsSaving(false);
    };

    const handleOpenModal = (expense = null) => {
        if (expense) {
            setEditingExpense(expense);
            setGiderTuru(expense.giderTuru || '');
            setTutar(expense.tutar || '');
            setFirmaAdi(expense.firmaAdi || '');
            setTarih(expense.tarih?.toDate ? expense.tarih.toDate() : new Date(expense.tarih));
            setAciklama(expense.aciklama || '');
        } else {
            resetForm();
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        resetForm();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!giderTuru || !tutar || !firmaAdi || !tarih) {
            alert("Lütfen zorunlu alanları (*) doldurunuz.");
            return;
        }

        const parsedTutar = Number(tutar.toString().replace(',','.'));
        if (isNaN(parsedTutar) || parsedTutar <= 0) {
            alert("Geçerli bir tutar giriniz.");
            return;
        }

        setIsSaving(true);

        const expData = {
            giderTuru,
            tutar: parsedTutar,
            firmaAdi,
            tarih: Timestamp.fromDate(tarih),
            aciklama,
            updatedAt: Timestamp.now()
        };

        try {
            if (editingExpense) {
                await updateDoc(doc(db, 'expenses', editingExpense.id), expData);
            } else {
                expData.createdAt = Timestamp.now();
                expData.createdBy = currentUser?.uid || 'Unknown';
                await addDoc(collection(db, 'expenses'), expData);
            }
            await refreshExpenses(); // cache'i tazele, kayıt listede hemen görünsün
            handleCloseModal();
        } catch (error) {
            console.error("Gider kaydedilirken hata:", error);
            alert("Kayıt sırasında bir hata oluştu.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id, title) => {
        if (window.confirm(`Bu gider kaydını (${title}) kalıcı olarak silmek istediğinize emin misiniz?`)) {
            try {
                await deleteDoc(doc(db, 'expenses', id));
                await refreshExpenses(); // cache'i tazele, silinen kayıt listeden düşsün
            } catch (error) {
                console.error("Gider silinirken hata:", error);
                alert("Silme işlemi başarısız.");
            }
        }
    };

    if (loading) return <Loading message="Giderler yükleniyor..." />;

    return (
        <Box>
            <PageHeader
                title="Gider Takibi"
                subtitle="İşletme giderlerini buradan takip edebilirsiniz."
                icon={<PaymentsIcon />}
            />
            {/* Filtre ve Üst Barlar */}
            <GlassCard sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            <FilterListIcon color="action" sx={{ mr: 1, alignSelf: 'center' }} />
                            <Button size="small" variant="outlined" onClick={() => handleDateFilterClick(startOfMonth(new Date()), endOfMonth(new Date()))}>Bu Ay</Button>
                            <Button size="small" variant="outlined" onClick={() => handleDateFilterClick(startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1)))}>Geçen Ay</Button>
                            <Button size="small" variant="outlined" onClick={() => handleDateFilterClick(startOfMonth(subMonths(new Date(), 2)), endOfMonth(subMonths(new Date(), 2)))}>Önceki Ay</Button>
                        </Stack>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                            <DatePicker 
                                label="Başlangıç" 
                                value={startDate} 
                                onChange={setStartDate} 
                                slotProps={{ textField: { size: 'small', sx: { width: 140 } } }} 
                            />
                            <DatePicker 
                                label="Bitiş" 
                                value={endDate} 
                                onChange={setEndDate} 
                                slotProps={{ textField: { size: 'small', sx: { width: 140 } } }} 
                            />
                            <Button variant="outlined" size="small" onClick={() => { setStartDate(null); setEndDate(null); }}>Temizle</Button>
                        </Box>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
                        Yeni Gider Ekle
                    </Button>
                </Box>
            </GlassCard>

            {/* Özet Kartları */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={3}>
                    <StatCard 
                        title="Dönem Toplam Gider" 
                        value={summaryData.total.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })} 
                        color="error.main" 
                    />
                </Grid>
                {/* En yüksek ilk 3 kategoriyi göster */}
                {summaryData.topCategories.map(([cat, val], idx) => (
                    <Grid item xs={12} sm={3} key={idx}>
                        <StatCard 
                            title={`${cat} Toplamı`} 
                            value={val.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })} 
                            color="info.main" 
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Listeleme */}
            <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Gider Kayıtları ({filteredExpenses.length})</Typography>
                    <Button variant="contained" color="success" onClick={handleExport} disabled={!filteredExpenses.length}>Excel Çıktısı</Button>
                </Box>
                <TableContainer sx={{ maxHeight: 500 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Tarih</TableCell>
                                <TableCell>Gider Türü</TableCell>
                                <TableCell>Firma / Kişi</TableCell>
                                <TableCell>Açıklama</TableCell>
                                <TableCell align="right">Tutar</TableCell>
                                <TableCell align="center">İşlem</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredExpenses.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ border: 0 }}>
                                        <EmptyState
                                            icon={<PaymentsIcon />}
                                            message="Gider kaydı bulunamadı."
                                            description="Bu tarih aralığında kaydedilmiş bir gider bulunmuyor."
                                        />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredExpenses.map((exp) => (
                                    <TableRow hover key={exp.id}>
                                        <TableCell>
                                            {exp.tarih?.toDate ? exp.tarih.toDate().toLocaleDateString('tr-TR') : '-'}
                                        </TableCell>
                                        <TableCell>{exp.giderTuru}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{exp.firmaAdi}</TableCell>
                                        <TableCell>{exp.aciklama || '-'}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                            {typeof exp.tutar === 'number' ? exp.tutar.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' }) : '-'}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton aria-label="Gideri düzenle" size="small" onClick={() => handleOpenModal(exp)} color="primary">
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton aria-label="Gideri sil" size="small" onClick={() => handleDelete(exp.id, exp.giderTuru)} color="error">
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </GlassCard>

            {/* Form Drawer / Modal */}
            <Dialog open={isModalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
                <DialogTitle>{editingExpense ? 'Gider Kaydını Düzenle' : 'Yeni Gider Ekle'}</DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Gider Türü</InputLabel>
                            <Select
                                value={giderTuru}
                                label="Gider Türü"
                                onChange={(e) => setGiderTuru(e.target.value)}
                            >
                                {GIDER_TURLERI.map((tur) => (
                                    <MenuItem key={tur} value={tur}>{tur}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField 
                            label="Firma / İlgili Kişi" 
                            value={firmaAdi} 
                            onChange={(e) => setFirmaAdi(e.target.value)} 
                            fullWidth 
                            required 
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    label="Tutar" 
                                    type="number" 
                                    value={tutar} 
                                    onChange={(e) => setTutar(e.target.value)} 
                                    fullWidth 
                                    required 
                                    InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DatePicker 
                                    label="Tarih *" 
                                    value={tarih} 
                                    onChange={setTarih} 
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                        </Grid>

                        <TextField 
                            label="Açıklama (Opsiyonel)" 
                            value={aciklama} 
                            onChange={(e) => setAciklama(e.target.value)} 
                            fullWidth 
                            multiline 
                            rows={3} 
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px' }}>
                    <Button onClick={handleCloseModal} disabled={isSaving}>İptal</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
                        {isSaving ? <CircularProgress size={24} /> : (editingExpense ? 'Güncelle' : 'Kaydet')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default GiderTakibi;
